import React, { createContext, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { emptyArray } from 'ui-components/default-props/empty';

import Global from '../../../store/global-selectors';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';
import { getAllIntegrations } from '../api/integrations-api';
import { useIntegrationPartnersFeatureGate } from '../../../feature-gates/use-integration-partners-feature-gate';

const IntegrationsContext = createContext();

const IntegrationsContextProvider = ({ children }) => {
  const [integrations, setIntegrations] = useAsyncSafeState(emptyArray);
  const integrationFeatureGate = useIntegrationPartnersFeatureGate();
  const allStores = useSelector(Global.selectSellerStores) ?? emptyArray;
  const sellerStores = useMemo(
    () => allStores.filter((x) => x.isActive).map((store) => ({ id: store.storeId, name: store.name })),
    [allStores],
  );

  const fetchIntegrations = useCallback(() => {
    getAllIntegrations().then((allIntegrations) => {
      const featureEnabledIntegrations = allIntegrations.filter((integration) => {
        const isFeatureEnabled = integrationFeatureGate[integration.name];
        const isOtherIntegrationsFeatureEnabled = integrationFeatureGate.OtherIntegrations;
        if (isFeatureEnabled) {
          return isFeatureEnabled;
        }
        // if integration does not have a feature then it falls under OtherIntegrations
        return isOtherIntegrationsFeatureEnabled;
      });
      setIntegrations(featureEnabledIntegrations);
    });
  }, [integrationFeatureGate, setIntegrations]);

  useOnComponentMount(fetchIntegrations);

  const value = useMemo(
    () => ({
      integrations,
      fetchIntegrations,
      sellerStores,
    }),
    [integrations, fetchIntegrations, sellerStores],
  );

  return <IntegrationsContext.Provider value={value}>{children}</IntegrationsContext.Provider>;
};

IntegrationsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { IntegrationsContext, IntegrationsContextProvider };
